import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import type { MarketingCampaignBudgetSegment } from "@common/interfaces/KYDMarketing";
import { scheduleInputToUtcIso } from "@common/utils/adsetSchedule";
import {
  scheduledSegmentMetaEditStates,
  snapUnixSecondsToMetaBudgetScheduleUtc,
} from "@common/utils/campaignBudgetMeta";
import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

type PhaseTimes = {
  time_start?: string | null;
  time_end?: string | null;
};

export type ScheduledBudgetPhaseRowUi = {
  isCompletedPhase: boolean;
  /** Boosted phase linked to Meta that cannot POST after advance + 3h filter. */
  isLockedPhase: boolean;
  lockReason?: string;
  capPhaseStartAtNow: boolean;
};

export type CreateScheduledBudgetEditPolicyParams = {
  linkedToMeta: boolean;
  /** Required when `linkedToMeta` so phase lock uses {@link scheduledSegmentMetaEditStates}. */
  scheduledBudget?: {
    type: "scheduled";
    segments: MarketingCampaignBudgetSegment[];
  };
  eventTimezone?: string;
  nowMs?: number;
};

/**
 * UI rules for scheduled budget phases when a campaign is linked to Meta.
 * Server-side freeze-on-save lives in `guidedEditCampaignBudget`; lock/publish rules use
 * {@link scheduledSegmentMetaEditStates} in `common`.
 */
export type ScheduledBudgetEditPolicy = {
  linkedToMeta: boolean;
  phaseRow: (
    phase: PhaseTimes,
    segmentIndex: number,
    eventTimezone: string | undefined
  ) => ScheduledBudgetPhaseRowUi;
  phaseStartPickerMin: (eventTimezone: string | undefined) => Dayjs | null;
};

/** Editor rows use event-wall datetimes; {@link scheduledSegmentMetaEditStates} expects UTC ISO. */
function scheduledBudgetSegmentsWallToUtcIso(
  segments: MarketingCampaignBudgetSegment[],
  eventTimezone: string | undefined
): MarketingCampaignBudgetSegment[] {
  const tz = (eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
  return segments.map((s) => ({
    ...s,
    time_start:
      scheduleInputToUtcIso(String(s.time_start ?? "").trim(), tz) ??
      String(s.time_start ?? ""),
    time_end:
      scheduleInputToUtcIso(String(s.time_end ?? "").trim(), tz) ??
      String(s.time_end ?? ""),
  }));
}

/** Form wall-time `time_end` vs now (editor rows are not persisted UTC ISO). */
function scheduledBudgetPhaseEndedOnWall(
  phase: PhaseTimes,
  eventTimezone: string | undefined,
  nowMs: number = Date.now()
): boolean {
  const tz = (eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
  const endIso =
    scheduleInputToUtcIso(String(phase.time_end ?? "").trim(), tz) ??
    String(phase.time_end ?? "").trim();
  const endMs = Date.parse(endIso);
  return Number.isFinite(endMs) && endMs <= nowMs;
}

export function createScheduledBudgetEditPolicy(
  params: CreateScheduledBudgetEditPolicyParams
): ScheduledBudgetEditPolicy {
  const { linkedToMeta, scheduledBudget, eventTimezone, nowMs } = params;
  const nowSec = Math.floor((nowMs ?? Date.now()) / 1000);

  const metaEditStates =
    linkedToMeta && scheduledBudget?.type === "scheduled"
      ? scheduledSegmentMetaEditStates({
          budget: {
            type: "scheduled",
            segments: scheduledBudgetSegmentsWallToUtcIso(
              scheduledBudget.segments,
              eventTimezone
            ),
          },
          timezone: (eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE,
          linkedToMeta: true,
          nowUnixSeconds: nowSec,
        })
      : null;

  return {
    linkedToMeta,
    phaseRow(phase, segmentIndex, tz) {
      if (!linkedToMeta) {
        return { isCompletedPhase: false, isLockedPhase: false, capPhaseStartAtNow: false };
      }
      const ended = scheduledBudgetPhaseEndedOnWall(phase, tz, nowMs);
      const metaRow = metaEditStates?.[segmentIndex];
      const isLockedPhase = metaRow?.state === "locked";
      return {
        isCompletedPhase: ended,
        isLockedPhase,
        lockReason: isLockedPhase ? metaRow?.lockReason : undefined,
        capPhaseStartAtNow: !ended && !isLockedPhase,
      };
    },
    phaseStartPickerMin(eventTimezone) {
      return linkedToMeta ? scheduledPhaseStartPickerMin(eventTimezone) : null;
    },
  };
}

/** Earliest allowed phase start on linked Meta campaigns (now, Meta 15m grid, in `eventTimezone`). */
export function scheduledPhaseStartPickerMin(
  eventTimezone: string | undefined
): Dayjs | null {
  const tz = (eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
  const nowSec = snapUnixSecondsToMetaBudgetScheduleUtc(
    Math.floor(Date.now() / 1000),
    "ceil"
  );
  const d = dayjs.unix(nowSec).tz(tz);
  return d.isValid() ? d : null;
}
